<template>
  <div class="hms-page">
    <Navigation />

    <div class="container-fluid hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header px-3">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-map-marker-radius me-2"></i>Facility Network — Geo Referencing</h4>
        </div>
        <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
      </div>

      <!-- STATS -->
      <div class="hms-stat-row px-3">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ branches.length }}</div><div class="hms-stat-lbl">Total Facilities</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ levelCounts.referral }}</div><div class="hms-stat-lbl">Level 5/6 Referral</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ levelCounts.l4 }}</div><div class="hms-stat-lbl">Level 4 (Sub-County)</div></div>
        <div class="hms-stat"><div class="hms-stat-num">{{ totalBeds }}</div><div class="hms-stat-lbl">Total Beds</div></div>
        <div class="hms-stat"><div class="hms-stat-num">{{ formatNumber(totalCatchment) }}</div><div class="hms-stat-lbl">Catchment Pop.</div></div>
      </div>

      <div class="hms-map-row">
        <!-- LEFT — Map -->
        <div class="hms-map-pane">
          <div id="hms-facility-map" class="hms-map-canvas"></div>
        </div>

        <!-- RIGHT — Facility list -->
        <div class="hms-list-pane">
          <div class="hms-card">
            <div class="hms-card-head">Facilities ({{ branches.length }})</div>
            <div class="hms-card-body p-0">
              <div v-for="b in branches" :key="b.branch_id"
                   class="hms-fac-row" :class="{ active: selected && selected.branch_id === b.branch_id }"
                   @click="focusFacility(b)">
                <div class="hms-fac-head">
                  <span class="hms-fac-name">{{ b.branch_name }}</span>
                  <span class="hms-pill" :class="levelPill(b.facility_level)">L{{ b.facility_level }}</span>
                </div>
                <div class="hms-muted-sm">{{ b.branch_code }} · {{ b.sub_county || '—' }} · {{ b.ward || '' }}</div>
                <div class="hms-fac-meta">
                  <span><i class="mdi mdi-bed"></i> {{ b.bed_capacity || 0 }}</span>
                  <span><i class="mdi mdi-account-group"></i> {{ formatNumber(b.catchment_population || 0) }}</span>
                  <span v-if="b.kmfl_code"><i class="mdi mdi-identifier"></i> KMFL {{ b.kmfl_code }}</span>
                </div>
                <div v-if="b.sha_fid" class="hms-muted-sm"><i class="mdi mdi-shield-key-outline"></i> SHA: {{ b.sha_fid }}</div>
                <div v-if="!b.latitude || !b.longitude" class="hms-no-coords"><i class="mdi mdi-alert"></i> No GPS coords</div>
              </div>
            </div>
          </div>

          <div v-if="selected" class="hms-card mt-3">
            <div class="hms-card-head">{{ selected.branch_name }}</div>
            <div class="hms-card-body">
              <div class="hms-detail-row"><span>Sub-county</span><strong>{{ selected.sub_county || '—' }}</strong></div>
              <div class="hms-detail-row"><span>Ward</span><strong>{{ selected.ward || '—' }}</strong></div>
              <div class="hms-detail-row"><span>KEPH Compliance</span><strong>{{ formatKeph(selected.keph_compliance) }}</strong></div>
              <div class="hms-detail-row"><span>Beds</span><strong>{{ selected.bed_capacity || 0 }}</strong></div>
              <div class="hms-detail-row"><span>Catchment</span><strong>{{ formatNumber(selected.catchment_population || 0) }}</strong></div>
              <div class="hms-detail-row"><span>Coordinates</span>
                <strong v-if="selected.latitude && selected.longitude">{{ Number(selected.latitude).toFixed(4) }}, {{ Number(selected.longitude).toFixed(4) }}</strong>
                <strong v-else>—</strong>
              </div>
              <div class="hms-detail-row"><span>Phone</span><strong>{{ selected.phone || '—' }}</strong></div>
              <div class="hms-detail-row"><span>Today's reg</span><strong>{{ selected.today_registrations || 0 }}</strong></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS  = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

function loadLeaflet() {
  return new Promise((resolve, reject) => {
    if (window.L) { resolve(window.L); return; }
    if (!document.querySelector('link[data-leaflet]')) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      link.dataset.leaflet = '1';
      document.head.appendChild(link);
    }
    if (!document.querySelector('script[data-leaflet]')) {
      var s = document.createElement('script');
      s.src = LEAFLET_JS;
      s.dataset.leaflet = '1';
      s.onload = () => resolve(window.L);
      s.onerror = reject;
      document.head.appendChild(s);
    } else {
      var poll = setInterval(() => {
        if (window.L) { clearInterval(poll); resolve(window.L); }
      }, 100);
    }
  });
}

export default {
  name: "FacilityMap",
  components: { Navigation },
  data() {
    return {
      branches: [],
      selected: null,
      map: null,
      markers: {}
    };
  },
  computed: {
    levelCounts() {
      const out = { referral: 0, l4: 0, lower: 0 };
      this.branches.forEach(b => {
        const l = parseInt(b.facility_level || 4);
        if (l >= 5) out.referral++;
        else if (l === 4) out.l4++;
        else out.lower++;
      });
      return out;
    },
    totalBeds() { return this.branches.reduce((s, b) => s + parseInt(b.bed_capacity || 0), 0); },
    totalCatchment() { return this.branches.reduce((s, b) => s + parseInt(b.catchment_population || 0), 0); }
  },
  async mounted() {
    await this.loadAll();
    await this.initMap();
  },
  methods: {
    async loadAll() {
      try {
        const r = await executeJson({ function: 'getBranches' }, hms);
        if (r.data && r.data.success) {
          this.branches = (r.data.data && r.data.data.Branches) || [];
        }
      } catch (e) { console.error(e); }
    },

    async initMap() {
      try {
        const L = await loadLeaflet();
        if (this.map) { this.map.remove(); this.map = null; }
        // Default center: Narok County
        const map = L.map('hms-facility-map').setView([-1.10, 35.85], 9);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 18,
          attribution: '&copy; OpenStreetMap'
        }).addTo(map);
        this.map = map;
        this.placeMarkers();
      } catch (e) {
        console.error('Map init failed', e);
      }
    },

    placeMarkers() {
      if (!this.map || !window.L) return;
      const L = window.L;
      Object.values(this.markers).forEach(m => m.remove());
      this.markers = {};
      const points = [];
      this.branches.forEach(b => {
        if (!b.latitude || !b.longitude) return;
        const lat = parseFloat(b.latitude); const lon = parseFloat(b.longitude);
        const isReferral = parseInt(b.facility_level) >= 5;
        const color = isReferral ? '#dc2626' : '#2563eb';
        const radius = isReferral ? 14 : 10;
        const marker = L.circleMarker([lat, lon], {
          radius: radius,
          fillColor: color,
          color: '#fff',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.85
        }).addTo(this.map);
        marker.bindTooltip(b.branch_name + ' (L' + b.facility_level + ')', { permanent: false, direction: 'top' });
        marker.bindPopup(
          '<strong>' + b.branch_name + '</strong><br>' +
          (b.sub_county || '') + ' · ' + (b.ward || '') + '<br>' +
          'Beds: ' + (b.bed_capacity || 0) + ' · Catchment: ' + (b.catchment_population ? Number(b.catchment_population).toLocaleString() : '—') + '<br>' +
          (b.kmfl_code ? 'KMFL: ' + b.kmfl_code + '<br>' : '') +
          (b.sha_fid ? 'SHA: ' + b.sha_fid : '')
        );
        marker.on('click', () => { this.selected = b; });
        this.markers[b.branch_id] = marker;
        points.push([lat, lon]);
      });
      if (points.length > 0) {
        this.map.fitBounds(points, { padding: [40, 40], maxZoom: 11 });
      }
    },

    focusFacility(b) {
      this.selected = b;
      if (!this.map || !b.latitude || !b.longitude) return;
      this.map.setView([parseFloat(b.latitude), parseFloat(b.longitude)], 12, { animate: true });
      const m = this.markers[b.branch_id];
      if (m) m.openPopup();
    },

    levelPill(l) {
      const lv = parseInt(l || 4);
      if (lv >= 5) return 'hms-pill-danger';
      if (lv === 4) return 'hms-pill-warn';
      return 'hms-pill-info';
    },
    formatKeph(k) {
      return ({ compliant: 'Compliant', partial: 'Partial', non_compliant: 'Non-compliant',
                assessing: 'Assessing', not_applicable: 'N/A' })[k] || k;
    },
    formatNumber(n) {
      const x = parseInt(n || 0);
      return x.toLocaleString();
    }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 12px 0 24px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 26px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-warning { color: #c05621; } .hms-stat-success { color: #276749; }

.hms-map-row { display: grid; grid-template-columns: 2.2fr 1fr; gap: 12px; padding: 0 12px; }
@media (max-width: 992px) { .hms-map-row { grid-template-columns: 1fr; } }

.hms-map-pane { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
.hms-map-canvas { height: 640px; width: 100%; }

.hms-list-pane { display: flex; flex-direction: column; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body.p-0 { padding: 0; }
.hms-card-body { padding: 14px; }

.hms-fac-row { padding: 10px 14px; border-bottom: 1px solid #edf2f7; cursor: pointer; }
.hms-fac-row:last-child { border-bottom: none; }
.hms-fac-row:hover { background: #f7fafc; }
.hms-fac-row.active { background: #ebf8ff; }
.hms-fac-head { display: flex; justify-content: space-between; align-items: center; gap: 6px; }
.hms-fac-name { font-weight: 600; color: #1a202c; font-size: 14px; }
.hms-fac-meta { display: flex; gap: 12px; margin-top: 4px; font-size: 12px; color: #4a5568; }
.hms-fac-meta .mdi { color: #a0aec0; }

.hms-no-coords { color: #c53030; font-size: 11px; margin-top: 4px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 700; }
.hms-pill-info   { background: #bee3f8; color: #2a4365; }
.hms-pill-warn   { background: #feebc8; color: #7b341e; }
.hms-pill-danger { background: #fed7d7; color: #742a2a; }

.hms-detail-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #edf2f7; font-size: 13px; }
.hms-detail-row:last-child { border-bottom: none; }
.hms-detail-row span { color: #718096; }
.hms-detail-row strong { color: #2d3748; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-blue { background: #2b6cb0; color: #fff; }

.hms-muted-sm { color: #a0aec0; font-size: 11px; margin-top: 2px; }
</style>
